import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LS_KEY = "wmm-active-site-id";

const SiteContext = createContext(null);

export const SiteProvider = ({ children }) => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSiteId, setActiveSiteIdState] = useState(() => localStorage.getItem(LS_KEY) || null);
  const [healthMap, setHealthMap] = useState({});
  const [healthLoading, setHealthLoading] = useState({});
  // Preview "Page details" strip (slug pills) - hidden by default, toggled
  // from the top bar's PAGE row.
  const [previewToolsOpen, setPreviewToolsOpen] = useState(false);
  // THE single WordPress connector (SiteSettingsSheet, rendered by TopBar).
  // Any "Connect WordPress" button anywhere opens this - never duplicate
  // connection forms on other screens.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const healthInFlight = useRef({});
  const healthMapRef = useRef({});
  useEffect(() => {
    healthMapRef.current = healthMap;
  }, [healthMap]);

  const refreshSites = useCallback(async () => {
    // Retry a few times so one transient failure (e.g. right after an SSO
    // handoff) never leaves the workspace switcher permanently empty.
    const attempt = async (n) => {
      try {
        const { data } = await axios.get(`${API}/sites`);
        const list = data.sites || [];
        setSites(list);
        setActiveSiteIdState((cur) => {
          const valid = cur && list.some((s) => s.id === cur);
          const next = valid ? cur : list[0]?.id || null;
          if (next) localStorage.setItem(LS_KEY, next);
          return next;
        });
      } catch (e) {
        if (n < 3) {
          await new Promise((r) => setTimeout(r, 1200 * (n + 1)));
          return attempt(n + 1);
        }
      }
      return undefined;
    };
    await attempt(0);
    setLoading(false);
  }, []);

  useEffect(() => {
    refreshSites();
  }, [refreshSites]);

  const setActiveSiteId = useCallback((id) => {
    localStorage.setItem(LS_KEY, id);
    setActiveSiteIdState(id);
    setPreviewToolsOpen(false); // collapse page details when switching workspaces
  }, []);

  const fetchHealth = useCallback(async (siteId, force = false) => {
    if (!siteId) return;
    if (!force && (healthInFlight.current[siteId] || healthMapRef.current[siteId])) return;
    healthInFlight.current[siteId] = true;
    setHealthLoading((m) => ({ ...m, [siteId]: true }));
    try {
      const { data } = await axios.get(`${API}/sites/${siteId}/health`);
      setHealthMap((m) => ({ ...m, [siteId]: data }));
    } catch (e) {
      setHealthMap((m) => ({ ...m, [siteId]: { error: true } }));
    } finally {
      healthInFlight.current[siteId] = false;
      setHealthLoading((m) => ({ ...m, [siteId]: false }));
    }
  }, []);

  // Health alerting: keep the active site's scorecard fresh so the bar can
  // flag connection/deploy failures the moment they happen.
  useEffect(() => {
    if (!activeSiteId) return undefined;
    fetchHealth(activeSiteId);
    const iv = setInterval(() => fetchHealth(activeSiteId, true), 120000);
    return () => clearInterval(iv);
  }, [activeSiteId, fetchHealth]);

  const value = useMemo(() => {
    const activeSite = sites.find((s) => s.id === activeSiteId) || null;
    return {
      sites,
      loading,
      activeSite,
      activeSiteId,
      setActiveSiteId,
      refreshSites,
      fetchHealth,
      healthMap,
      healthLoading,
      previewToolsOpen,
      setPreviewToolsOpen,
      settingsOpen,
      setSettingsOpen,
    };
  }, [sites, loading, activeSiteId, setActiveSiteId, refreshSites, fetchHealth, healthMap, healthLoading, previewToolsOpen, settingsOpen]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

export const useSite = () => {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error("useSite must be used inside <SiteProvider>");
  return ctx;
};
